/**
 * Row derivation for the gated patient balance table (gdn-2943).
 *
 * Pure functions over the financial-summary payload. Local types still
 * mirror the backend's reconciliation view and activity lines
 * (gdn-2940/2941) pending gdn-2998; every field degrades gracefully when
 * the deployed API predates it.
 */

import type {
  AccountActivityRowView,
  EncounterLedgerGroup,
  LedgerEntryItem,
  PatientDueStatus,
  PatientInvoiceHistoryItem,
} from '../../store';

export type { AccountActivityRowView };

export interface ReconciliationView {
  state: string;
  estimated_cents: number;
  confirmed_cents: number;
  collected_cents: number;
  remaining_due_cents: number;
  credit_cents: number;
  headline: string;
  explanation: string;
  recommended_action: string;
}

export interface ActivityLine {
  id: string;
  label: string;
  detail?: string | null;
  amount_cents: number;
  tone: 'ok' | 'due' | 'info';
}

/** Fields gdn-2940/2941 add to EncounterLedgerGroup. Codegen now carries
 * all of them; swapping to the generated types turns ~115 reconciliation
 * reads across ten files into possibly-undefined ones, so it has its own
 * pass (gdn-2998) rather than riding along here. */
interface EncounterGroupV2 {
  claim_billed_cents?: number;
  activity_lines?: ActivityLine[];
  adjustments?: { insurance_adjustment_cents?: number } | null;
  patient_responsibility_reconciliation?: ReconciliationView | null;
}

/** A declined card-on-file charge riding an invoice (gdn-3064). Present
 * only when a payment attempt has actually failed.
 *
 * The failures are STRIPE'S retries, not the patient's attempts —
 * invoices auto-advance, so Stripe re-presents the card on its own
 * schedule and the patient usually did nothing. That is why the count
 * lives here as detail context and never in the row copy, where it would
 * read as an accusation about a person who wasn't involved. */
export interface RowInvoiceDecline {
  /** ISO date of the most recent declined attempt. */
  failedDate: string;
  /** How many automatic attempts have failed. Context, not copy. */
  failureCount: number;
  /** Stripe's decline reason, stored unparsed — rendered verbatim. The
   * decline-code set is not ours to enumerate. */
  failureReason: string | null;
  /** ISO date of Stripe's next automatic attempt; null when Stripe has
   * stopped. This is the discriminator between the two decline states:
   * retries pending very probably resolves itself (Stripe's dunning
   * recovers roughly nine declines in ten, measured gdn-3065), while
   * exhausted goes nowhere until someone acts. */
  nextAttemptDate: string | null;
}

/** The active invoice a row's balance is riding on (gdn-3001). Null means
 * nothing has gone out, which is the difference between a balance nobody
 * has actioned and one already being pursued — the row has to say which,
 * because the right verb depends on it. */
export interface RowInvoice {
  invoiceId: string;
  /** ISO date the invoice was sent. */
  sentDate: string | null;
  /** ISO date of the most recent reminder, null if never chased. */
  lastRemindedDate: string | null;
  reminderCount: number;
  /** The invoice's declined-charge state, if a card attempt has failed. */
  decline: RowInvoiceDecline | null;
}

export type BalanceBucket = 'attention' | 'waiting' | 'hold' | 'settled';

/** The row note is a second line under the label, so it has to stay on one
 * line at the table's width. Drop the year for the current year — where
 * nearly every open invoice sits — and keep it when it would otherwise be
 * ambiguous. */
const formatNoteDate = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};


/** The decline's one-line row copy: the state and what happens next,
 * never the attempt count (see RowInvoiceDecline). The two states demand
 * opposite responses — retries pending is reassurance ("this very
 * probably resolves itself"), no retries left is the one-in-ten that
 * will not — so each names its own future rather than sharing a vague
 * "payment failed". */
export const declineNoteText = (decline: RowInvoiceDecline): string => {
  const declined = `Card declined ${formatNoteDate(decline.failedDate)}`;
  return decline.nextAttemptDate
    ? `${declined} · Stripe retries ${formatNoteDate(decline.nextAttemptDate)}`
    : `${declined} · no retries left`;
};

/** One clause, not two: the description column is the flexible one and both
 * clauses truncate at table width, which would hide the very fact the line
 * exists to surface. Show the state that decides the row's next move — a
 * decline outranks the invoice/reminder history because it changes what
 * the money is waiting on — and keep the full sequence in the tooltip. */
export const invoiceNoteText = (invoice: RowInvoice): string => {
  if (invoice.decline) return declineNoteText(invoice.decline);
  if (invoice.lastRemindedDate) {
    const times = invoice.reminderCount > 1 ? ` ×${invoice.reminderCount}` : '';
    return `Reminded${times} ${formatNoteDate(invoice.lastRemindedDate)}`;
  }
  return invoice.sentDate ? `Invoiced ${formatNoteDate(invoice.sentDate)}` : 'Invoiced';
};

/** The decline's full story, for the hover: here the count is context —
 * Stripe's schedule — rather than an accusation, and the issuer's reason
 * appears verbatim ("Your card has insufficient funds." beats any
 * paraphrase). */
export const declineNoteDetail = (decline: RowInvoiceDecline): string => {
  const attempts =
    decline.failureCount > 1
      ? `Stripe's automatic attempts have been declined ${decline.failureCount} times, most recently ${formatNoteDate(decline.failedDate)}.`
      : `A card payment was declined ${formatNoteDate(decline.failedDate)}.`;
  const reason = decline.failureReason ? ` “${decline.failureReason}”` : '';
  const next = decline.nextAttemptDate
    ? `Stripe retries automatically ${formatNoteDate(decline.nextAttemptDate)} — most declined charges recover without any action.`
    : 'Stripe has stopped retrying — this invoice will not collect on its own.';
  return `${attempts}${reason} ${next}`;
};

/** The full sequence, for the hover that already explains the row. */
export const invoiceNoteDetail = (invoice: RowInvoice): string => {
  const sent = invoice.sentDate
    ? `Invoiced ${formatNoteDate(invoice.sentDate)}`
    : 'Invoiced';
  let note: string;
  if (!invoice.lastRemindedDate) {
    note = `${sent}, no reminder sent yet.`;
  } else {
    const times =
      invoice.reminderCount > 1 ? `${invoice.reminderCount} reminders` : 'reminder';
    note = `${sent}, ${times} — most recent ${formatNoteDate(invoice.lastRemindedDate)}.`;
  }
  return invoice.decline ? `${note} ${declineNoteDetail(invoice.decline)}` : note;
};


/** One credit-side event behind the unapplied-credit row. */
export interface CreditSourceEntry {
  date: string | null;
  label: string;
  amountCents: number;
  /** The ledger entry this came from — the target a verb acts on. */
  entryId: string;
  /** Ledger entry type, lowercase ('payment', 'reversal', ...). Scopes
   * which verbs may be offered; see removablePaymentsOf. */
  entryType: string;
  /** Proven payment_reversal source in this patient's ledger; display history stays intact. */
  removed?: boolean;
}

export interface ReceiptRow {
  label: string;
  amountCents: number;
  deduction: boolean;
}

export interface BalanceRow {
  kind: 'encounter';
  key: string;
  encounterId: string;
  date: string | null;
  label: string;
  bucket: BalanceBucket;
  outcome: { label: string; tone: 'quiet' | 'owes' | 'credit' | 'hold' };
  /** Patient share cell text; null renders a muted dash. */
  share: { text: string; estimated: boolean } | null;
  collectedCents: number;
  /** Receipt rows for the share hover; null when the claim math isn't
   * available (pre-adjudication or pre-deploy payload). */
  receipt: ReceiptRow[] | null;
  /** Lifecycle lines for the collected hover; empty pre-deploy. */
  activity: ActivityLine[];
  /** The active invoice covering this visit, if one is out. */
  invoice: RowInvoice | null;
  /** The encounter's raw ledger balance. Reconciliation describes only
   * *confirmed* responsibility, so an unfinalised estimate lives here and
   * nowhere else — without it the headline breakdown cannot close. */
  balanceCents: number;
  reconciliation: ReconciliationView | null;
}

/** A non-encounter item interleaved into the same timeline — the prototype
 * round's "different kind of row" treatment: a positive kind label, one
 * amount, no navigation. Replaces the separate Account Activity section
 * under the gate. Preferred source is the server's lifecycle rollup
 * (charge + its payment collapse to one settled row); raw ledger entries
 * are the fallback when the rollup is unavailable. */
export interface AccountRow {
  kind: 'account';
  key: string;
  date: string | null;
  /** Positive kind label: "Added charge — Late cancellation fee". */
  label: string;
  /** Rollup rows: unsigned (state lives in tone/detail). Raw fallback
   * rows: signed — negative reduces what the patient owes. */
  amountCents: number;
  tone: 'ok' | 'due' | 'info';
  /** Lifecycle state in plain language ("Paid.", "Invoiced to the
   * patient.") — shown in the outcome column. Null on raw fallback. */
  detail: string | null;
  /** MECE with encounter rows: due → attention, ok → settled, info → waiting. */
  bucket: BalanceBucket;
  /** Which amount column the row's number belongs in: charges are the
   * patient's share; payments/credits are money collected. */
  column: 'share' | 'collected';
  /** Active invoice this outstanding charge rides on — enables the
   * invoice's verbs (send reminder) on the row. */
  invoice: RowInvoice | null;
  /** The collapsed contra pair's annotation: credit-side events (payments,
   * reversals) behind an unapplied-credit row. History, not arithmetic —
   * but since gdn-3255 each carries its ledger entry id and type, so a
   * verb can act on one. */
  sources: CreditSourceEntry[];
  /** Original payment identity for removal; history does not add available credit. */
  recordedPayment?: CreditSourceEntry;
  /** The charge line this row stands for, when it is one. The rollup keys
   * charge rows as `charge_line:{id}`; the account credit row and raw
   * fallback entries have none — and neither can be assigned to an
   * encounter (gdn-3106). */
  chargeLineId: string | null;
  /** The charge's patient-due lifecycle state, when it has one.
   *
   * `null` means this row is not a charge line — the synthetic
   * unapplied-credit row is collapsed ledger entries with no such
   * lifecycle — or the deployed API predates the field. Both are
   * correctly not-assignable.
   *
   * Carried as the status rather than a collapsed `isPaid` on purpose:
   * a boolean would bake one verb's eligibility into a general-purpose
   * row, and the next verb would have to re-ask the question. It also
   * keeps written-off distinguishable from unknown, which `tone` cannot
   * do — `tone: 'ok'` covers paid and written-off alike, which is what
   * made this field necessary. */
  patientDueStatus: PatientDueStatus | null;
}

export type BalanceTableRow = BalanceRow | AccountRow;

/** A decline leaves the invoice open, so `last_payment_failed_at` is the
 * only signal one happened — and therefore the key. A payload predating
 * gdn-3061's fields simply never yields a decline. Exported so the panel
 * can rebuild the decline for the retry dialog from the same record. */
export const invoiceDeclineOf = (
  inv: PatientInvoiceHistoryItem,
): RowInvoiceDecline | null =>
  inv.last_payment_failed_at
    ? {
        failedDate: inv.last_payment_failed_at.slice(0, 10),
        failureCount: inv.payment_failure_count ?? 0,
        failureReason: inv.last_payment_failure_reason ?? null,
        nextAttemptDate: inv.next_payment_attempt_at
          ? inv.next_payment_attempt_at.slice(0, 10)
          : null,
      }
    : null;

/** The summary returns invoice history as a flat list; rows reference
 * invoices by id, so index it once rather than scanning per row. */
const invoiceIndexOf = (
  invoices: PatientInvoiceHistoryItem[],
): Map<string, RowInvoice> =>
  new Map(
    invoices.map(inv => [
      inv.invoice_id,
      {
        invoiceId: inv.invoice_id,
        // created_at is a timestamp; the row only ever shows the day.
        sentDate: inv.created_at ? inv.created_at.slice(0, 10) : null,
        lastRemindedDate: inv.last_reminded_at
          ? inv.last_reminded_at.slice(0, 10)
          : null,
        reminderCount: inv.reminder_count ?? 0,
        decline: invoiceDeclineOf(inv),
      },
    ]),
  );

/** First active invoice among the ids a row carries. The server already
 * filters these to active statuses, so a void or paid invoice never
 * reaches here and can't make a settled row look chased.
 *
 * The invoice history is capped at 100, so an id can be genuinely active
 * and still absent from it. Falling back to the bare id keeps the row's
 * invoice verbs working and loses only the "Invoiced <date>" line —
 * dropping the row's verbs because we couldn't find its send date would
 * be the worse failure. */
const invoiceFor = (
  ids: string[] | undefined,
  index: Map<string, RowInvoice>,
): RowInvoice | null => {
  const [first] = ids ?? [];
  if (!first) return null;
  for (const id of ids ?? []) {
    const found = index.get(id);
    if (found) return found;
  }
  return {
    invoiceId: first,
    sentDate: null,
    lastRemindedDate: null,
    reminderCount: 0,
    decline: null,
  };
};

/** The rollup keys charge rows `charge_line:{uuid}`; anything else (the
 * account credit row) is not a charge line. */
const CHARGE_LINE_PREFIX_RE = /^charge_line:(.+)$/;

const accountBucketOf = (tone: AccountRow['tone']): BalanceBucket =>
  tone === 'due' ? 'attention' : tone === 'info' ? 'waiting' : 'settled';

const toAccountRowFromRollup = (
  row: AccountActivityRowView,
  invoiceIndex: Map<string, RowInvoice>,
  removedPaymentIds: ReadonlySet<string>,
): AccountRow => ({
  kind: 'account',
  key: `account-${row.id}`,
  date: row.date ?? null,
  label: row.label,
  amountCents: row.amount_cents,
  tone: row.tone,
  detail: row.detail ?? null,
  bucket: accountBucketOf(row.tone),
  // Charge-line rows are the patient's share of account charges; the
  // unapplied-credit row is money received.
  column: row.id.startsWith('charge_line:') ? 'share' : 'collected',
  invoice: invoiceFor(row.invoice_id ? [row.invoice_id] : [], invoiceIndex),
  sources: (row.sources ?? []).map(src => ({
    date: src.date ?? null,
    label: src.label,
    amountCents: src.amount_cents,
    entryId: src.entry_id,
    entryType: src.entry_type,
    ...(src.entry_type === 'payment' && removedPaymentIds.has(src.entry_id) ? { removed: true } : {}),
  })),
  chargeLineId: CHARGE_LINE_PREFIX_RE.exec(row.id)?.[1] ?? null,
  patientDueStatus: row.patient_due_status ?? null,
});

// The ledger's PAYMENT_REVERSAL_SOURCE_PREFIX, passed through source_ref.id by
// the financial summary. Match the exact original ID, never amount or display copy.
const PAYMENT_REVERSAL_SOURCE_PREFIX = 'payment_reversal:';
const removedPaymentIdsOf = (entries: LedgerEntryItem[]): ReadonlySet<string> =>
  new Set(entries.flatMap(entry =>
    entry.entry_type === 'reversal' && entry.party_type === 'patient' &&
    entry.source_ref?.id?.startsWith(PAYMENT_REVERSAL_SOURCE_PREFIX)
      ? [entry.source_ref.id.slice(PAYMENT_REVERSAL_SOURCE_PREFIX.length)]
      : [],
  ));

const recordedPaymentOf = (
  entry: LedgerEntryItem,
  removedPaymentIds: ReadonlySet<string>,
): CreditSourceEntry | undefined =>
  entry.entry_type === 'payment' && entry.party_type === 'patient'
    ? {
        date: entry.created_at ? entry.created_at.slice(0, 10) : null,
        label: entry.description || entry.entry_type_label,
        amountCents: Math.abs(entry.amount_cents),
        entryId: entry.entry_id,
        entryType: entry.entry_type,
        ...(removedPaymentIds.has(entry.entry_id) ? { removed: true } : {}),
      }
    : undefined;

/** Original payment history stays visible even when no unapplied credit remains. */
const recordedPaymentRows = (
  entries: LedgerEntryItem[],
  removedPaymentIds: ReadonlySet<string>,
  removalAvailable: boolean,
): AccountRow[] => {
  const payments = new Map<string, CreditSourceEntry>();
  for (const entry of entries) {
    const payment = recordedPaymentOf(entry, removedPaymentIds);
    if (payment) payments.set(payment.entryId, payment);
  }
  return [...payments.values()].map(payment => ({
    kind: 'account',
    key: `account-payment-${payment.entryId}`,
    date: payment.date,
    label: `Recorded payment — ${payment.label}`,
    amountCents: payment.amountCents,
    tone: 'ok',
    detail: payment.removed
      ? 'Removed. Original recorded amount.'
      : removalAvailable ? 'Original recorded amount.' : 'Recorded for a visit. Original amount.',
    bucket: 'settled',
    column: 'collected',
    invoice: null,
    sources: [],
    chargeLineId: null,
    patientDueStatus: null,
    recordedPayment: removalAvailable ? payment : undefined,
  }));
};

const toAccountRowFromEntry = (
  entry: LedgerEntryItem,
  removedPaymentIds: ReadonlySet<string>,
): AccountRow => ({
  kind: 'account',
  key: `account-${entry.entry_id}`,
  date: entry.created_at ? entry.created_at.slice(0, 10) : null,
  label: entry.description
    ? `${entry.entry_type_label} — ${entry.description}`
    : entry.entry_type_label,
  recordedPayment: recordedPaymentOf(entry, removedPaymentIds),
  amountCents: entry.amount_cents,
  tone: entry.amount_cents < 0 ? 'ok' : 'due',
  detail: entry.entry_type === 'payment' && entry.party_type === 'patient' && removedPaymentIds.has(entry.entry_id)
    ? 'Removed. Original recorded amount.' : null,
  bucket: entry.amount_cents < 0 ? 'settled' : 'attention',
  column: entry.amount_cents < 0 ? 'collected' : 'share',
  invoice: null,
  sources: [],
  // Raw fallback rows are ledger entries, not charge lines — the
  // assignment verb has no id to act on and must not be offered.
  chargeLineId: null,
  patientDueStatus: null,
});

const HOLD_STATES = new Set([
  'secondary_pending_suspended',
  'denial_or_claim_review',
]);

/** Balance an encounter carries beyond its confirmed reconciliation.
 *
 * Reconciliation nets *confirmed* responsibility against collections, so a
 * COPAY_ESTIMATED line still sitting on the ledger shows up in neither
 * remaining_due nor credit. That gap is this residue, and it is only
 * nameable when the reconciliation reports an estimate. */
export const unfinalisedEstimateCents = (
  recon: ReconciliationView | null,
  balanceCents: number,
): number => {
  if (!recon || (recon.estimated_cents ?? 0) <= 0) return 0;
  const explained = recon.remaining_due_cents - recon.credit_cents;
  return balanceCents - explained;
};

export const bucketOf = (
  recon: ReconciliationView | null,
  fallbackBalanceCents = 0,
): BalanceBucket => {
  // No reconciliation (pre-deploy payload, ERA-processing gap): fall back
  // to the raw ledger balance — an outstanding balance must never read as
  // settled just because the read model is missing.
  if (!recon) return fallbackBalanceCents !== 0 ? 'attention' : 'settled';
  if (HOLD_STATES.has(recon.state)) return 'hold';
  // Held after a refund waits on the biller's decision (gdn-4242), whatever
  // the cents say — the same rule its "Held after refund" chip follows.
  if (recon.state === 'refund_hold') return 'attention';
  if (recon.remaining_due_cents > 0 || recon.credit_cents > 0) return 'attention';
  // Pre-adjudication: the share is an estimate and can still swing either
  // way when the ERA lands — in flight, not finished business.
  if (recon.state === 'estimate_pending_era') return 'waiting';
  // Confirmed money can be square while an estimate on the same encounter
  // is still open; that is waiting, not settled.
  if (unfinalisedEstimateCents(recon, fallbackBalanceCents) > 0) return 'waiting';
  return 'settled';
};

export const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

const outcomeOf = (
  recon: ReconciliationView | null,
  fallbackBalanceCents = 0,
): BalanceRow['outcome'] => {
  if (!recon) {
    // Mirror bucketOf's fallback: the raw balance speaks when the read
    // model is missing.
    if (fallbackBalanceCents > 0) {
      return { label: `Owes ${formatCents(fallbackBalanceCents)}`, tone: 'owes' };
    }
    if (fallbackBalanceCents < 0) {
      return { label: `Credit ${formatCents(-fallbackBalanceCents)}`, tone: 'credit' };
    }
    return { label: '✓ Settled', tone: 'quiet' };
  }
  switch (recon.state) {
    case 'secondary_pending_suspended':
      return { label: 'Hold — secondary', tone: 'hold' };
    case 'denial_or_claim_review':
      return { label: 'Hold — review denial', tone: 'hold' };
    case 'refund_hold':
      // A refund took back money that paid this visit (gdn-4242). The
      // patient owes it again, but nothing collects it until a biller bills
      // or writes it off, so "Owes" would read as already collectable. Not
      // a HOLD_STATE: those wait on someone else, and this waits on the
      // biller, so it stays in Needs attention.
      return { label: 'Held after refund', tone: 'owes' };
    case 'estimate_pending_era':
      // An uncollected estimate buckets as attention — the chip must agree
      // with the bucket, not read as a calm wait.
      if (recon.remaining_due_cents > 0) {
        return {
          label: `Owes est. ${formatCents(recon.remaining_due_cents)}`,
          tone: 'owes',
        };
      }
      return { label: 'Awaiting ERA', tone: 'quiet' };
    default:
      break;
  }
  if (recon.remaining_due_cents > 0) {
    return { label: `Owes ${formatCents(recon.remaining_due_cents)}`, tone: 'owes' };
  }
  if (recon.credit_cents > 0) {
    return { label: `Credit ${formatCents(recon.credit_cents)}`, tone: 'credit' };
  }
  if (unfinalisedEstimateCents(recon, fallbackBalanceCents) > 0) {
    return { label: 'Awaiting ERA', tone: 'quiet' };
  }
  return { label: '✓ Settled', tone: 'quiet' };
};

/** Receipt: billed − contractual adjustment − insurance paid = patient
 * share. Only rendered when the claim-side numbers exist AND the math is
 * self-consistent — a receipt that doesn't add up is worse than none. */
const receiptOf = (
  enc: EncounterLedgerGroup & EncounterGroupV2,
  recon: ReconciliationView | null,
  subjectCap: string,
): ReceiptRow[] | null => {
  const billed = enc.claim_billed_cents ?? 0;
  if (!recon || billed <= 0 || recon.confirmed_cents <= 0) return null;
  const adjustment = enc.adjustments?.insurance_adjustment_cents ?? 0;
  const insurancePaid = enc.insurance_paid_cents ?? 0;
  if (billed - adjustment - insurancePaid !== recon.confirmed_cents) return null;
  return [
    { label: 'Claim billed', amountCents: billed, deduction: false },
    { label: 'Contractual adjustment', amountCents: adjustment, deduction: true },
    { label: 'Insurance paid', amountCents: insurancePaid, deduction: true },
    {
      label: `${subjectCap} share`,
      amountCents: recon.confirmed_cents,
      deduction: false,
    },
  ];
};

/** How the headline balance adds up, derived from the rows: each
 * encounter's remaining due (+) or credit (−), each outstanding account
 * charge (+), each unapplied credit (−). Returns null unless the lines
 * sum EXACTLY to the balance — a breakdown that doesn't add up is worse
 * than none (same guard as the claim receipt). */
export const balanceBreakdown = (
  rows: BalanceTableRow[],
  balanceCents: number,
): { label: string; amountCents: number }[] | null => {
  const lines: { label: string; amountCents: number }[] = [];
  for (const row of rows) {
    if (row.kind === 'encounter') {
      const recon = row.reconciliation;
      if (!recon) {
        // Fallback rows carry their raw balance in the outcome; without a
        // machine-readable amount here, refuse to guess.
        if (row.outcome.tone !== 'quiet') return null;
        continue;
      }
      if (recon.remaining_due_cents > 0) {
        lines.push({ label: `${row.label} — owed`, amountCents: recon.remaining_due_cents });
      }
      if (recon.credit_cents > 0) {
        lines.push({ label: `${row.label} — credit`, amountCents: -recon.credit_cents });
      }
      // An estimate still on the ledger is real money in the balance but
      // absent from confirmed responsibility. Name it — flagged as
      // provisional — rather than leaving the arithmetic short, which
      // suppressed the whole tooltip. Residue we cannot name still falls
      // through to the sum guard below.
      const estimateCents = unfinalisedEstimateCents(recon, row.balanceCents);
      if (estimateCents !== 0) {
        lines.push({
          label: `${row.label} — estimated, awaiting ERA`,
          amountCents: estimateCents,
        });
      }
    } else {
      if (row.tone === 'due') {
        lines.push({ label: `${row.label} — owed`, amountCents: row.amountCents });
      } else if (row.amountCents < 0 || row.key === 'account-account:unapplied-credit') {
        lines.push({ label: `${row.label} — credit`, amountCents: -Math.abs(row.amountCents) });
      }
    }
  }
  if (lines.length === 0) return null;
  const total = lines.reduce((sum, line) => sum + line.amountCents, 0);
  return total === balanceCents ? lines : null;
};

/** The rollup's single unapplied-credit row id (gdn-2948), key-prefixed. */
const ACCOUNT_CREDIT_KEY = 'account-account:unapplied-credit';

/** Money that can be moved by the allocation verb (gdn-2950): an
 * encounter's over-collection, or the account's unapplied credit. */
export interface CreditSource {
  kind: 'encounter_credit' | 'account_credit';
  /** Null for the account-level credit. */
  sourceEncounterId: string | null;
  label: string;
  /** What can actually move — for the account credit this is the BE's
   * allocatable figure, which nets out the account's own open charges. */
  creditCents: number;
  /** The part of the row's displayed credit that can't move because it's
   * held against the account's open charges. Zero for encounter sources
   * (recon.credit_cents already matches the BE's allocatable math). */
  heldBackCents: number;
  /** What a charge on the account can take (gdn-4321): the account's
   * unapplied credit, which is the charge verb's cap. It can exceed
   * `creditCents`, because the credit a visit can't take is exactly what
   * covers those charges. Absent or 0 when no charge can take it. */
  chargeCreditCents?: number;
}

/** An encounter that can receive an allocation: reconciled with a
 * confirmed remaining due. Fallback rows (no reconciliation) are never
 * targets — money must not move onto a row we can't reconcile. */
export interface CreditTarget {
  encounterId: string;
  label: string;
  date: string | null;
  remainingDueCents: number;
}

/** Whether a row can source credit, and when it cannot, whether that is
 * because the verb does not apply to this kind of row at all or because
 * it applies but is currently blocked.
 *
 * The distinction is the whole point (gdn-2994): a row that simply has no
 * credit should stay quiet, while a row that *shows* a credit but cannot
 * move it owes the biller a reason. Hold states are deliberately
 * 'not_applicable' rather than blocked — the row's outcome chip already
 * says "Hold — secondary" / "Hold — review denial", so repeating it on
 * every verb would say the same thing three times. */
export type CreditEligibility =
  | { status: 'eligible'; source: CreditSource }
  | { status: 'blocked'; reason: string }
  | { status: 'not_applicable' };

/** Why the account credit can't go to a visit or back to the patient: it
 * covers the account's own open charges (gdn-4321). */
const CREDIT_COVERS_ACCOUNT_CHARGES = "This credit covers the account's open charges.";
const REFUND_APPLY_INSTEAD =
  "This credit covers the account's open charges. Apply it to them instead.";
const ACCOUNT_CHARGES_ON_INVOICE =
  "The account's open charges are on an invoice. Void it first to apply credit.";
const NO_VISIT_OWES = 'No visit currently owes';

export const creditEligibilityOf = (
  row: BalanceTableRow,
  accountAllocatableCreditCents: number | null = null,
): CreditEligibility => {
  if (row.kind === 'encounter') {
    // Hold classification outranks the cents figures (same ordering as
    // bucketOf): a held balance is provisional — secondary may pay, a
    // denial review may restate it — so its credit must not move.
    if (row.reconciliation && HOLD_STATES.has(row.reconciliation.state)) {
      return { status: 'not_applicable' };
    }
    const credit = row.reconciliation?.credit_cents ?? 0;
    if (credit <= 0) return { status: 'not_applicable' };
    return {
      status: 'eligible',
      source: {
        kind: 'encounter_credit',
        sourceEncounterId: row.encounterId,
        label: row.label,
        creditCents: credit,
        heldBackCents: 0,
      },
    };
  }
  // Only the server rollup's credit row — raw fallback rows predate the
  // allocation endpoint and carry no reconciled amount to move.
  if (row.key !== ACCOUNT_CREDIT_KEY || row.amountCents <= 0) {
    return { status: 'not_applicable' };
  }
  // Older API without the allocatable figure: offer nothing rather than
  // the gross, and say nothing rather than guess a reason.
  if (accountAllocatableCreditCents === null) return { status: 'not_applicable' };
  if (accountAllocatableCreditCents <= 0) {
    return { status: 'blocked', reason: CREDIT_COVERS_ACCOUNT_CHARGES };
  }
  const creditCents = Math.min(accountAllocatableCreditCents, row.amountCents);
  return {
    status: 'eligible',
    source: {
      kind: 'account_credit',
      sourceEncounterId: null,
      label: row.label,
      creditCents,
      heldBackCents: row.amountCents - creditCents,
    },
  };
};

/** The eligible source alone — retained for callers that only need to
 * know whether the verb can run. */
export const creditSourceOf = (
  row: BalanceTableRow,
  accountAllocatableCreditCents: number | null = null,
): CreditSource | null => {
  const eligibility = creditEligibilityOf(row, accountAllocatableCreditCents);
  return eligibility.status === 'eligible' ? eligibility.source : null;
};

/**
 * Whether a row is a stranded charge the assignment verb can repair
 * (gdn-3106): a PAID, account-level charge line whose money really
 * belonged to an encounter.
 *
 * Both server-side refusals knowable before the click are enforced here,
 * so a biller never discovers one by being refused:
 *
 * - **Account-level** is free and structural: these rows come from the
 *   account activity rollup, which is account-level (non-encounter) by
 *   construction, and a raw fallback row carries no charge line at all.
 * - **PAID** is read from `patientDueStatus`. The verb relocates a
 *   *payment*, so anything else — written off, still invoiced, or a row
 *   with no lifecycle at all — has nothing to move.
 *
 * Deliberately NOT inferred from `tone`/`detail`: the rollup gives PAID
 * and WRITTEN_OFF an identical `tone: 'ok'`, so the only other
 * discriminator is the display string "Paid.", and matching copy would
 * silently start mis-offering the verb the day someone reworded it.
 * smartscribe-server#4167 added the status field for exactly this.
 */
export const assignableChargeOf = (
  row: BalanceTableRow,
): { chargeLineId: string; amountCents: number } | null => {
  if (row.kind !== 'account') return null;
  if (!row.chargeLineId) return null;
  if (row.patientDueStatus !== 'paid') return null;
  return { chargeLineId: row.chargeLineId, amountCents: row.amountCents };
};

/**
 * A charge a refund left held on the account (gdn-4242): the biller bills
 * the patient for it or writes it off. Read from the status, never from the
 * row's copy; the rollup shows a held row's remaining amount.
 */
export const heldChargeOf = (
  row: BalanceTableRow,
): { chargeLineId: string; amountCents: number } | null => {
  if (row.kind !== 'account' || !row.chargeLineId) return null;
  if (row.patientDueStatus !== 'held_after_refund') return null;
  return { chargeLineId: row.chargeLineId, amountCents: row.amountCents };
};

/**
 * A charge line this row can reverse (gdn-3187).
 *
 * Deliberately coarser than assignableChargeOf next door, which requires
 * `patientDueStatus === 'paid'`. Reverse takes ANY charge-line-backed
 * account row, whatever its status, for two reasons:
 *
 *  - A PAID line is the point, not an exclusion. Reversing one is the
 *    gdn-2915 flow: the payment stays and becomes account credit.
 *  - The server is the eligibility authority. It refuses adjudicated,
 *    invoiced and ERA-netted lines with a stated reason, and that reason
 *    is the canonical copy. Pre-filtering here would convert "the server
 *    tells you why not" into "the button isn't there", which is the
 *    failure this verb already shipped once and had caught in review.
 *
 * So the asymmetry with assignableChargeOf is a decision, not an
 * oversight: assign RELOCATES money and needs the line settled first;
 * reverse UNDOES it and needs the server's judgement, not ours.
 *
 * Encounter rows are excluded because they roll up several lines — there
 * is no single line for the verb to act on. Reversing an encounter-linked
 * line needs a per-line view, which is a known gap: PatientDetailView,
 * the per-line surface, has been unmounted since 548b2caa (2026-04-10).
 */
export const reversibleChargeOf = (
  row: BalanceTableRow,
): { chargeLineId: string; amountCents: number; hasAppliedPayment: boolean } | null => {
  if (row.kind !== 'account') return null;
  if (!row.chargeLineId) return null;
  return {
    chargeLineId: row.chargeLineId,
    amountCents: row.amountCents,
    // Decides the confirmation copy only — the server decides whether the
    // reversal runs at all.
    hasAppliedPayment: row.patientDueStatus === 'paid',
  };
};

/**
 * The original recorded payment or payments behind a credit row a biller could remove
 * as a mistake (gdn-3255).
 *
 * Structural filter only — PAYMENT entries. Transfers, refunds and
 * reversals are history with nothing to remove, so offering the verb on
 * them would promise an act that does not exist. Everything beyond that
 * is the server's call: it refuses a Stripe-sourced payment (refund it
 * instead) and unsafe allocation targets, in sentences the
 * dialog renders verbatim. The FE does not pre-judge either.
 *
 * 'payment' lowercase matches the vocabulary already in use across the
 * ledger surfaces (PatientBalanceCard, LedgerTabContent) and the
 * schema's own examples — not a guess.
 */
export const removablePaymentsOf = (row: BalanceTableRow): CreditSourceEntry[] => {
  if (row.kind !== 'account') return [];
  if (row.recordedPayment) return row.recordedPayment.removed ? [] : [row.recordedPayment];
  return row.key === ACCOUNT_CREDIT_KEY
    ? row.sources.filter(src => src.entryType === 'payment' && !src.removed)
    : [];
};

/** Shown on a charge the account's credit can't pay until its invoice is
 * voided: the server refuses credit on an invoiced charge, because the
 * open invoice could still be paid as well. */
export const ACCOUNT_CHARGE_ON_INVOICE_REASON = 'On an open invoice. Void it first to apply credit.';

/** A charge on the account that Apply credit lists (gdn-4321). */
export interface AccountChargeTarget {
  chargeLineId: string;
  label: string;
  date: string | null;
  /** What the charge still owes: the rollup shows an owing row's remainder. */
  remainingDueCents: number;
  /** Why credit can't pay it yet; null when it can. */
  blockedReason: string | null;
}

/** Null: credit can pay the charge. A string: listed, but blocked for that
 * reason. Undefined: not listed at all. */
const blockedReasonOf = (status: PatientDueStatus | null): string | null | undefined => {
  if (status === 'confirmed') return null;
  if (status === 'invoiced') return ACCOUNT_CHARGE_ON_INVOICE_REASON;
  return undefined;
};

const accountChargeTargetOf = (row: BalanceTableRow): AccountChargeTarget[] => {
  if (row.kind !== 'account' || !row.chargeLineId || row.amountCents <= 0) return [];
  const blockedReason = blockedReasonOf(row.patientDueStatus);
  if (blockedReason === undefined) return [];
  return [
    {
      chargeLineId: row.chargeLineId,
      label: row.label,
      date: row.date,
      remainingDueCents: row.amountCents,
      blockedReason,
    },
  ];
};

/**
 * Charges on the account that Apply credit lists, oldest first (gdn-4321):
 * confirmed ones the account's credit can pay, and ones on an open invoice,
 * listed but blocked. Read from each row's status, never its copy. Anything
 * else (paid, written off, in collections) takes no credit and isn't listed.
 */
export const accountChargeTargetsOf = (rows: BalanceTableRow[]): AccountChargeTarget[] =>
  rows
    .flatMap(accountChargeTargetOf)
    .sort((a, b) => (a.date ?? '9999-12-31').localeCompare(b.date ?? '9999-12-31'));

/** What the credit verbs on a row can use (gdn-4321). */
export interface CreditContext {
  /** What a visit may take from the account's credit: the ledger-net
   * account_allocatable_credit_cents. Null on an older server. */
  allocatableCents: number | null;
  /** What a charge on the account may take: the account's unapplied
   * credit (summary.credit_balance_cents), the charge verb's own cap.
   * Null when unknown. */
  unappliedCents: number | null;
  visitTargets: CreditTarget[];
  accountCharges: AccountChargeTarget[];
}

export interface CreditVerbs {
  apply: CreditEligibility;
  refund: CreditEligibility;
}

const isAccountCredit = (row: BalanceTableRow): row is AccountRow =>
  row.kind === 'account' && row.key === ACCOUNT_CREDIT_KEY && row.amountCents > 0;

/** A visit-only source can run only when some visit owes. */
const visitOnly = (eligibility: CreditEligibility, ctx: CreditContext): CreditEligibility =>
  eligibility.status === 'eligible' && ctx.visitTargets.length === 0
    ? { status: 'blocked', reason: NO_VISIT_OWES }
    : eligibility;

const accountApplyBlockedReason = (
  base: CreditEligibility,
  chargeCreditCents: number,
  ctx: CreditContext,
): string => {
  if (chargeCreditCents > 0 && ctx.accountCharges.length > 0) return ACCOUNT_CHARGES_ON_INVOICE;
  return base.status === 'blocked' ? base.reason : NO_VISIT_OWES;
};

/**
 * Apply credit and Refund on a row, together (gdn-4321), because on the
 * account's credit row each depends on the other.
 *
 * The account credit can pay a charge on the account even when a visit
 * can't take any of it: the credit a visit can't take is exactly the money
 * that covers those charges. So Apply credit runs when either a visit or a
 * confirmed account charge can take something. Refund stays blocked while
 * the credit covers the account's charges; the server's refund cap agrees.
 */
export const creditVerbsOf = (row: BalanceTableRow, ctx: CreditContext): CreditVerbs => {
  const base = creditEligibilityOf(row, ctx.allocatableCents);
  if (!isAccountCredit(row)) return { apply: visitOnly(base, ctx), refund: base };

  const chargeCreditCents = Math.max(0, Math.min(ctx.unappliedCents ?? 0, row.amountCents));
  const chargesCan =
    chargeCreditCents > 0 && ctx.accountCharges.some(charge => charge.blockedReason === null);
  const visitsCan = base.status === 'eligible' && ctx.visitTargets.length > 0;
  const refund: CreditEligibility =
    base.status === 'blocked' && chargesCan
      ? { status: 'blocked', reason: REFUND_APPLY_INSTEAD }
      : base;
  if (!visitsCan && !chargesCan) {
    if (base.status === 'not_applicable' && chargeCreditCents === 0) return { apply: base, refund };
    return {
      apply: { status: 'blocked', reason: accountApplyBlockedReason(base, chargeCreditCents, ctx) },
      refund,
    };
  }
  const visitSource = base.status === 'eligible' ? base.source : null;
  return {
    apply: {
      status: 'eligible',
      source: {
        kind: 'account_credit',
        sourceEncounterId: null,
        label: row.label,
        creditCents: visitSource?.creditCents ?? 0,
        heldBackCents: visitSource?.heldBackCents ?? row.amountCents,
        chargeCreditCents: chargesCan ? chargeCreditCents : 0,
      },
    },
    refund,
  };
};

/** The account credit's Apply credit source — what the panel's offer after
 * an assignment and the Apply credit modal both open the dialog on.
 *
 * Credit a visit could take, with no visit or charge to take it yet, still
 * comes back as the visit source: the dialog then opens and says there is
 * nothing to apply it to yet (gdn-4176), where null would have the caller
 * say the credit covers the account's charges, or that there is none. */
export const accountCreditSourceOf = (
  rows: BalanceTableRow[],
  ctx: CreditContext,
): CreditSource | null => {
  for (const row of rows) {
    if (!isAccountCredit(row)) continue;
    const { apply } = creditVerbsOf(row, ctx);
    if (apply.status === 'eligible') return apply.source;
    const visitCredit = creditEligibilityOf(row, ctx.allocatableCents);
    if (visitCredit.status === 'eligible') return visitCredit.source;
  }
  return null;
};

export const creditTargetsOf = (rows: BalanceTableRow[]): CreditTarget[] =>
  rows.flatMap(row =>
    row.kind === 'encounter' &&
    row.reconciliation &&
    // A held balance is provisional (see creditSourceOf) — money must not
    // land on it until the hold resolves and the due figure is final.
    !HOLD_STATES.has(row.reconciliation.state) &&
    row.reconciliation.remaining_due_cents > 0
      ? [
          {
            encounterId: row.encounterId,
            label: row.label,
            date: row.date,
            remainingDueCents: row.reconciliation.remaining_due_cents,
          },
        ]
      : [],
  );

export const toBalanceRows = (
  encounters: EncounterLedgerGroup[],
  accountEntries: LedgerEntryItem[] = [],
  accountActivityRows: AccountActivityRowView[] | null = null,
  /** Subject-language capitalized singular ("Patient" | "Client"). */
  subjectCap = 'Patient',
  /** The summary's invoice history, so a row can say an invoice is already
   * out instead of reading as un-actioned (gdn-3001). */
  invoices: PatientInvoiceHistoryItem[] = [],
): BalanceTableRow[] => {
  const invoiceIndex = invoiceIndexOf(invoices);
  const encounterRows: BalanceTableRow[] = encounters
    .filter(
      (enc): enc is EncounterLedgerGroup & { encounter_id: string } =>
        enc.encounter_id != null,
    )
    .map(enc => {
      const v2 = enc as EncounterLedgerGroup & EncounterGroupV2;
      const recon = v2.patient_responsibility_reconciliation ?? null;
      const isHold = recon ? HOLD_STATES.has(recon.state) : false;
      // Raw ledger balance — the authority when the reconciliation read
      // model is absent (mirrors the ungated EncounterWhyRows derivation).
      const fallbackBalanceCents =
        enc.patient_balance_cents ??
        (enc.patient_due_cents ?? 0) - (enc.patient_paid_cents ?? 0);

      let share: BalanceRow['share'] = null;
      if (recon && !isHold) {
        if (recon.state === 'estimate_pending_era') {
          share =
            recon.estimated_cents > 0
              ? { text: `est. ${formatCents(recon.estimated_cents)}`, estimated: true }
              : null;
        } else if (recon.confirmed_cents > 0) {
          share = { text: formatCents(recon.confirmed_cents), estimated: false };
        }
      }

      return {
        kind: 'encounter' as const,
        key: enc.encounter_id,
        encounterId: enc.encounter_id,
        date: enc.service_date ?? null,
        label:
          [enc.encounter_type_label, enc.provider_name]
            .filter(Boolean)
            .join(' · ') || 'Encounter',
        bucket: bucketOf(recon, fallbackBalanceCents),
        outcome: outcomeOf(recon, fallbackBalanceCents),
        share,
        collectedCents: recon?.collected_cents ?? enc.patient_paid_cents ?? 0,
        balanceCents: fallbackBalanceCents,
        receipt: receiptOf(v2, recon, subjectCap),
        activity: v2.activity_lines ?? [],
        invoice: invoiceFor(enc.linked_invoice_ids, invoiceIndex),
        reconciliation: recon,
      };
    });
  const encounterEntries = encounters.flatMap(enc => enc.entries ?? []);
  const removedPaymentIds = removedPaymentIdsOf([...accountEntries, ...encounterEntries]);
  const accountEntryIds = new Set(accountEntries.map(entry => entry.entry_id));
  // Encounter-linked originals are visible history, but Remove is account-only:
  // the server unconditionally refuses on_a_visit. Account entries win duplicates.
  const encounterPayments = recordedPaymentRows(
    encounterEntries.filter(entry => !accountEntryIds.has(entry.entry_id)),
    removedPaymentIds,
    false,
  );
  const accountRows =
    accountActivityRows !== null
      ? [
          ...accountActivityRows.map(row => toAccountRowFromRollup(row, invoiceIndex, removedPaymentIds)),
          ...recordedPaymentRows(accountEntries, removedPaymentIds, true),
        ]
      : accountEntries.map(entry => toAccountRowFromEntry(entry, removedPaymentIds));
  return [...encounterRows, ...accountRows, ...encounterPayments].sort(
    (a, b) => (b.date ?? '').localeCompare(a.date ?? ''),
  );
};
